import { GameMetricsService } from './game-metrics-service';
import { AssetCatalogueService } from 'src/games/common/phaser/game/services/asset-catalogue-service';
import {
  BlackjackAssetType,
  BlackjackSpriteType,
  BlackjackStylesType,
  BlackjackTranslationsType,
} from '../model/blackjack-types';
import { assertNever, createDimensionsMap } from 'src/games/common/helper';
import { BlackjackListener, ChipDenomination, EventQueue } from 'game-logic';
import { GameTranslationService } from 'src/games/common/phaser/game/services/game-translation-service';
import { BlackjackGameRunner } from '../../model/blackjack-game-runner';
import { InterfaceController } from '../controllers/interface-controller';
import { GameThemeService } from 'src/games/common/phaser/game/services/game-theme-service';
import { ThemeService } from 'src/app/core/services/theme/theme.service';

export type InterfaceEvents = {
  onBetCleared: { totalBetAmount: number; currentBalance: number };
  onBetIncreased: {
    chip: ChipDenomination;
    totalBetAmount: number;
    currentBalance: number;
  };
  onBetAllIn: {
    chips: ChipDenomination[];
    totalBetAmount: number;
    currentBalance: number;
  };
};

export class GameServices {
  public readonly interfaceEventQueue = new EventQueue<InterfaceEvents>();
  public readonly assetCatalogueService: AssetCatalogueService<
    BlackjackAssetType,
    BlackjackSpriteType
  >;
  public readonly gameTranslationService: GameTranslationService<BlackjackTranslationsType>;
  public readonly gameThemeService: GameThemeService<BlackjackStylesType>;
  public readonly gameMetricsService: GameMetricsService;
  public readonly blackjackGameRunner: BlackjackGameRunner;
  public readonly interfaceController: InterfaceController;

  constructor(
    public gameListener: BlackjackListener,
    themeService: ThemeService,
    readonly useLocalDealer: boolean,
  ) {
    this.assetCatalogueService = new AssetCatalogueService(
      BlackjackAssetType,
      GameServices.constructAssets(),
      BlackjackSpriteType,
      GameServices.constructSprites(),
    );
    this.gameTranslationService = new GameTranslationService(
      'game.blackjack',
      Object.values(BlackjackTranslationsType),
    );
    this.gameThemeService = new GameThemeService(
      themeService.gameFontFamily(),
      Object.values(BlackjackStylesType),
    );
    this.gameMetricsService = new GameMetricsService();
    this.blackjackGameRunner = new BlackjackGameRunner(gameListener);
    this.interfaceController = new InterfaceController(
      this.blackjackGameRunner,
      this.interfaceEventQueue,
      gameListener,
    );
  }

  private static constructAssets() {
    return createDimensionsMap(BlackjackAssetType, (value) => {
      switch (value) {
        case BlackjackAssetType.StartButton:
          return { width: 564, height: 128 };
        case BlackjackAssetType.PlayIcon:
          return { width: 128, height: 128 };
        case BlackjackAssetType.Chip10:
          return { width: 256, height: 263 };
        case BlackjackAssetType.Chip50:
          return { width: 256, height: 263 };
        case BlackjackAssetType.Chip100:
          return { width: 256, height: 263 };
        case BlackjackAssetType.Chip500:
          return { width: 256, height: 263 };
        case BlackjackAssetType.Chip1k:
          return { width: 256, height: 263 };
        case BlackjackAssetType.Chip5k:
          return { width: 256, height: 263 };
        case BlackjackAssetType.Chip10k:
          return { width: 256, height: 263 };
        case BlackjackAssetType.Chip50k:
          return { width: 256, height: 263 };
        case BlackjackAssetType.Chip100k:
          return { width: 256, height: 263 };
        case BlackjackAssetType.ChipSmall10:
          return { width: 256, height: 263 };
        case BlackjackAssetType.ChipSmall50:
          return { width: 256, height: 263 };
        case BlackjackAssetType.ChipSmall100:
          return { width: 256, height: 263 };
        case BlackjackAssetType.ChipSmall500:
          return { width: 256, height: 263 };
        case BlackjackAssetType.ChipSmall1k:
          return { width: 256, height: 263 };
        case BlackjackAssetType.ChipSmall5k:
          return { width: 256, height: 263 };
        case BlackjackAssetType.ChipSmall10k:
          return { width: 256, height: 263 };
        case BlackjackAssetType.ChipSmall50k:
          return { width: 256, height: 263 };
        case BlackjackAssetType.ChipSmall100k:
          return { width: 256, height: 263 };
        case BlackjackAssetType.CardBack:
          return { width: 256, height: 384 };
        case BlackjackAssetType.CardClub:
          return { width: 256, height: 384 };
        case BlackjackAssetType.CardDiamond:
          return { width: 256, height: 384 };
        case BlackjackAssetType.CardHeart:
          return { width: 256, height: 384 };
        case BlackjackAssetType.CardSpade:
          return { width: 256, height: 384 };
        case BlackjackAssetType.CardShadow:
          return { width: 315, height: 443 };
        default:
          assertNever(value, 'Undefined game asset');
      }
    });
  }

  private static constructSprites() {
    return createDimensionsMap(BlackjackSpriteType, (value) => {
      switch (value) {
        case BlackjackSpriteType.NumbersLargePink:
          return { frameWidth: 170, frameHeight: 195 };
        case BlackjackSpriteType.NumbersLargeBlack:
          return { frameWidth: 170, frameHeight: 195 };
        case BlackjackSpriteType.NumbersSmallPink:
          return { frameWidth: 68, frameHeight: 70 };
        case BlackjackSpriteType.NumbersSmallBlack:
          return { frameWidth: 68, frameHeight: 70 };
        default:
          assertNever(value, 'Undefined game sprite');
      }
    });
  }
}
